define(function (require, exports, module) {
    'use strict';

    const { logger } = require('lib/logger');
    const app = require('Cloud/Application/app');
    const config = require('Cloud/config');
    const MicroEvent = require('mrg-microevent');
    const features = require('Cloud/Application/Features');
    const env = require('../env');
    const delay = require('../async/delay');
    const backendRequest = require('./backendRequest');
    const backendDispatcher = require('./backendDispatcher');
    const backendResponseTextOfPut = require('./backendResponseTextOfPut');
    const backendResponseTextOfPost = require('./backendResponseTextOfPost');
    const cloudFileSystem = require('../cloud/cloudFileSystem');
    const fileSystemFileReader = require('../fs/fileSystemFileReader');
    const UploadReason = require('../../UploadReason');
    const UploadCancel = require('../../UploadCancel');
    const HttpError = require('../../errors/HttpError');
    const IllegalContentFail = require('../../fails/IllegalContentFail');
    const UnsupportedFolderTransferFail = require('../../fails/UnsupportedFolderTransferFail');
    const FileTooLargeError = require('../../errors/FileTooLargeError');
    const WrongResultError = require('../../errors/WrongResultError');
    const FileWithoutHashFail = require('../../fails/FileWithoutHashFail');
    const ConnectionFail = require('../../fails/ConnectionFail');
    const FileReaderError = require('../../fails/FileReaderError');
    const HashCalcError = require('../../fails/HashCalcError');
    const UploaderData = require('./UploaderData');
    const { store: reduxStore } = require('reactApp/store');
    const { updateUploadFilesAction } = require('reactApp/modules/uploadList/uploadList.module');
    const { sendGaUploader } = require('reactApp/modules/uploadList/uploadList.helpers');
    const { UploadResumableCheckAPICall } = require('reactApp/api/UploadResumableCheckAPICall');
    const { EFileError, EFileStatus } = require('reactApp/modules/uploadList/uploadList.model');
    const { HashCalculator } = require('reactApp/modules/file/hashCalculator');
    const { isUploadResumeAvailabe } = require('reactApp/modules/features/features.helpers');
    const { sendKaktamLog } = require('reactApp/utils/ga');
    const { EHashLibType } = require('Cloud/webWorkers/types');
    const { isHashCalcLibCrypto, isHashCalcLibWasm } = require('reactApp/appHelpers/featuresHelpers');
    const { sendDwh, sendXray } = require('utils/ga');

    const MB = 1024 * 1024;
    const fileReadBlockLength = 100 * MB;
    const IS_REACT_PAGE = config.get('REACT_PAGE');
    const UPLOAD_API_POLLING_TIME = 4 * 60 * 60 * 1000;
    const UPLOAD_API_PING_INTERVAL = 5000;

    const getWaitTimeFromHeader = (headerVal) => {
        return Math.max(headerVal * 1000 || 60000, 10000);
    };

    const sendMetricsToDwh = (uploader, size, receivedBytes = null) => {
        size = size || uploader._file?.size;

        sendDwh({
            eventCategory: 'uploader',
            action: 'upload',
            label: 'metrics',
            dwhData: {
                diskReadTime: uploader._metrics.diskReadTime,
                hashCalcTime: uploader._metrics.hashCalcTime,
                putTime: uploader._metrics.putTime || 0,
                pollTime: uploader._metrics.pollTime || 0,
                totalTime: getPerfDiffInMs(uploader._metrics.startTime),
                fileSize: size,
                prevUploadedBytes: receivedBytes ?? 0,
            },
        });

        if (!size) {
            return;
        }

        const koeff = size / MB;

        sendXray(['uploader', 'upload', 'metric'], {
            diskReadTime: Math.round(uploader._metrics.diskReadTime / koeff),
            hashCalcTime: Math.round(uploader._metrics.hashCalcTime / koeff),
            putTime: Math.round(uploader._metrics.putTime / koeff) || 0,
            pollTime: Math.round(uploader._metrics.pollTime / koeff) || 0,
            totalTime: Math.round(getPerfDiffInMs(uploader._metrics.startTime) / koeff),
        });
    };

    const getPerfDiffInMs = (startTime) => Date.now() - startTime;

    /**
     * @constructor
     * @extends {MicroEvent}
     * @param {UploadDescriptor} descriptor
     */
    function Uploader(descriptor) {
        MicroEvent.call(this);

        this._file = descriptor.get('file');
        this._descriptor = descriptor;
    }

    Object.assign(Uploader, {
        /**
         * Событие прогресса загрузки файла.
         * @static
         * @type {string}
         */
        EVENT_PROGRESS: 'progress',

        /**
         * Ошибка загрузки файла, после которой будет выполнена повторная попытка.
         * @static
         * @type {string}
         */
        EVENT_RETRY: 'retry',

        /**
         * HTTP-метод загузки файла (PUT или POST).
         * CLOUDWEB-7724: в Firefox 56 обнаружились проблемы с PUT,
         * поэтому добавлен специальный параметр фичи.
         * @type {string}
         */
        HTTP_METHOD: features.getFeatureParam('newUpload', env.isFirefox ? 'firefoxMethod' : 'method', 'POST'),

        /**
         * Максимальное количество попыток повторения загрузки.
         * @static
         * @type {string}
         */
        MAX_RETRIES_COUNT: features.getFeatureParam('newUpload', 'maxRetriesCount', 3),

        /**
         * Максимальный размер файла, для которого
         * можно повторять попытки загрузки  (в мегабайтах).
         * @static
         * @type {string}
         */
        MAX_RETRYABLE_SIZE: features.getFeatureParam('newUpload', 'maxRetryableSize', 30) * MB,

        /**
         * Время задержки между повторами в миллисекундах (в миллисекундах).
         * @static
         * @type {number}
         */
        RETRY_DELAY: features.getFeatureParam('newUpload', 'retryDelay', 1000),

        /**
         * Увеличивать время ожидания между повторными попытками загрузки.
         * @static
         * @type {boolean}
         */
        IS_INCREMENTAL_RETRY_DELAY: features.getFeatureParam('newUpload', 'incrementalRetryDelay', true),

        /**
         * Время задержки между EVENT_PROGRESS (в миллисекундах).
         * @static
         * @type {number}
         */
        PROGRESS_DELAY: features.getFeatureParam('newUpload', 'progressDelay', 300),

        /**
         * @static
         * @type {Array.<number>}
         */
        retryableHttpStatuses: [
            ConnectionFail.CONNECTION_FAILURE,
            HttpError.REQUEST_TIMEOUT,
            HttpError.INTERNAL_SERVER_ERROR,
            HttpError.BAD_GATEWAY,
            HttpError.SERVICE_UNAVAILABLE,
            HttpError.GATEWAY_TIMEOUT,
        ],

        /**
         * @static
         * @param {number} status – HTTP status
         * @returns {boolean}
         */
        isRetryableHttpStatus(status) {
            return Uploader.retryableHttpStatuses.includes(status);
        },

        /**
         * @static
         * @param {number} readyState – xhr.readyState
         * @returns {boolean}
         */
        isCancelableXhrState(readyState) {
            const UNSENT = XMLHttpRequest.UNSENT;
            const DONE = XMLHttpRequest.DONE;
            return UNSENT < readyState && readyState < DONE;
        },
    });

    Uploader.prototype = Object.assign(Object.create(MicroEvent.prototype), {
        constructor: Uploader,

        /**
         * Адрес сервера для загрузки файла, полученный от балансера.
         * @type {number}
         */
        _url: '',

        /**
         * Номер попытки загрузки файла.
         * @type {number}
         */
        _attempt: 0,

        /**
         * Резолвит промис, который возвращает метод upload.
         * @type {Function}
         */
        _resolve: null,

        /**
         * Реджектит промис, который возвращает метод upload.
         * @type {Function}
         */
        _reject: null,

        /**
         * @type {File}
         */
        _file: null,

        /**
         * File hash, calculated on client
         */
        _hash: null,

        /**
         * @type {Promise}
         */
        _retryDelay: null,

        /**
         * @type {number}
         */
        _onXhrProgressCallTime: 0,

        /**
         * @type {number}
         */
        _uploadStartTime: -1,

        /**
         * @returns {boolean}
         */
        _canRetry() {
            const hasRetryableSize = this._file.size <= Uploader.MAX_RETRYABLE_SIZE;
            const hasAttempt = this._attempt < Uploader.MAX_RETRIES_COUNT;
            return hasRetryableSize && hasAttempt && !isUploadResumeAvailabe;
        },

        /**
         * @returns {UploaderData}
         */
        _getData() {
            const xhr = this._xhr;
            const params = {
                url: this._url,
                attempt: this._attempt,
            };

            if (xhr) {
                params.status = xhr.status;
                params.responseText = xhr.responseText;
            }

            return new UploaderData(params);
        },

        /**
         * @param {Event} event
         * @param {UploaderData} data
         */
        _onXhrAbort(event, data) {
            const source = UploadReason.SOURCE_WEB_CLIENT;
            const stack = new Error('UploadCancel');
            const reason = new UploadCancel(stack, source);
            const retryDelay = this._retryDelay;

            if (retryDelay) {
                retryDelay.cancel(reason);
            }

            data.error = reason;
            this._fail(data);
        },

        /**
         * @param {Event} event
         * @param {UploaderData} data
         */
        _onXhrError(event, data) {
            const status = data.status;
            const source = UploadReason.SOURCE_BACKEND;
            let stack;
            let reason;
            let error;

            if (status === HttpError.ILLEGAL) {
                stack = new Error('IllegalContentFail');
                reason = new IllegalContentFail(stack, source);
            } else if (status === HttpError.TOO_LARGE) {
                stack = new Error('FileTooLargeError');
                reason = new FileTooLargeError(stack, source);
            } else if (status) {
                stack = new Error('HttpError');
                reason = new HttpError(stack, source, status, data.responseText);
            } else {
                stack = new Error('ConnectionFail');
                reason = new ConnectionFail(stack, source);
                error = EFileError.CONNECTION_ERROR;
            }

            data.error = reason;

            const isRetryable = Uploader.isRetryableHttpStatus(status);
            const canRentry = this._canRetry();

            if (isRetryable && canRentry) {
                this._retry(data);
            } else {
                this._fail(data);

                const uploader = this;
                const descriptor = uploader._descriptor;

                sendGaUploader('file', error);

                if (descriptor) {
                    reduxStore.dispatch(
                        updateUploadFilesAction({
                            descriptorId: descriptor.id,
                            status: error === EFileError.CONNECTION_ERROR ? EFileStatus.PAUSED : EFileStatus.ERROR,
                            error,
                        })
                    );
                }
            }
        },

        /**
         * @param {Event} event
         * @param {UploaderData} data
         */
        _onXhrLoad(event, data) {
            const HTTP_OK = 200;
            const HTTP_CREATED = 201;
            const status = data.status;

            if (status === HTTP_OK || status === HTTP_CREATED) {
                const responseText = data.responseText;
                let responseData;

                if (Uploader.HTTP_METHOD === 'POST') {
                    responseData = backendResponseTextOfPost.parse(responseText);
                } else {
                    responseData = backendResponseTextOfPut.parse(responseText);
                    responseData.size = this._file.size;
                }

                Object.assign(data, responseData);

                const error = responseData.error;

                if (error) {
                    const canRentry = this._canRetry();

                    if (error.isRetryable && canRentry) {
                        this._retry(data);
                    } else {
                        this._fail(data);
                    }
                } else {
                    const kilobytes = data.size / 1024;
                    const seconds = (Date.now() - this._uploadStartTime) / 1000;

                    data.speed = parseFloat((kilobytes / seconds).toFixed(1));
                    this._done(data);
                }
            } else {
                this._onXhrError({}, data);
            }
        },

        /**
         * @param {Event} event
         * @param {UploaderData} data
         */
        _onXhrProgress(event, data) {
            const now = Date.now();

            if (now - this._onXhrProgressCallTime > Uploader.PROGRESS_DELAY) {
                this._onXhrProgressCallTime = now;

                const total = data.size || event.total;
                const loaded = event.loaded + (data.startBytes || 0);
                let progress;

                if (total && loaded) {
                    progress = (loaded * 100) / total;
                    progress = Math.round(progress);
                    progress = Math.min(progress, 100);
                } else {
                    progress = 0;
                }

                data.progress = progress;
                data.loaded = loaded;

                this.emit(Uploader.EVENT_PROGRESS, data);
            }
        },

        /**
         * @param {Event} event
         */
        handleEvent(event) {
            const uploader = this;
            const promise = new Promise(function (resolve) {
                const data = uploader._getData();

                switch (event.type) {
                    case 'abort':
                        uploader._onXhrAbort(event, data);

                        break;

                    case 'error':
                        uploader._onXhrError(event, data);

                        break;

                    case 'load':
                        uploader._onXhrLoad(event, data);

                        break;

                    case 'progress':
                        uploader._onXhrProgress(event, data);

                        break;
                }

                resolve();
            });

            promise.catch(function (error) {
                const data = uploader._getData();

                data.error = error;
                uploader._fail(data);
            });
        },

        /**
         * @param {XMLHttpRequest} xhr
         */
        _listenXhr(xhr) {
            xhr.addEventListener('abort', this);
            xhr.addEventListener('error', this);
            xhr.addEventListener('load', this);
            xhr.upload.addEventListener('progress', this);
        },

        /**
         * @param {XMLHttpRequest} xhr
         */
        _stopListeningXhr(xhr) {
            if (typeof xhr.removeEventListener !== 'function') {
                return;
            }

            xhr.removeEventListener('abort', this);
            xhr.removeEventListener('error', this);
            xhr.removeEventListener('load', this);
            xhr.upload.removeEventListener('progress', this);
        },

        /**
         * @param {UploaderData} data
         */
        _retry(data) {
            const uploader = this;

            const xhr = uploader._xhr;

            if (xhr) {
                uploader._stopListeningXhr(xhr);
            }

            this._attempt++;

            const RETRY_DELAY = Uploader.RETRY_DELAY;
            let promise;

            if (RETRY_DELAY) {
                if (Uploader.IS_INCREMENTAL_RETRY_DELAY) {
                    promise = delay(RETRY_DELAY * this._attempt);
                } else {
                    promise = delay(RETRY_DELAY);
                }
            } else {
                promise = Promise.resolve();
            }

            uploader._retryDelay = promise;
            promise = promise.then(
                function () {
                    uploader._retryDelay = null;
                    uploader.emit(Uploader.EVENT_RETRY, data);
                },
                function (reason) {
                    uploader._retryDelay = null;

                    return Promise.reject(reason);
                }
            );

            // Проверяет, доступен ли аплоадер для загрузки файла.
            promise = promise.then(function () {
                return backendDispatcher.ping(uploader._url, uploader._descriptor);
            });

            // Проверяет, доступен ли хотя бы nginx сервер для загрузки файла.
            promise = promise.catch(function (error) {
                let promise = Promise.resolve();

                if (error instanceof ConnectionFail) {
                    promise = backendDispatcher.pingNginx(uploader._url, uploader._descriptor);
                }

                promise = promise.then(
                    function () {
                        return Promise.reject(error);
                    },
                    function () {
                        return Promise.reject(error);
                    }
                );

                return promise;
            });

            // Получает новый адрес сервера для загрузки файла,
            // если старый адрес недоступен или сервер ответил ошибкой.
            promise = promise.catch(function (error) {
                let promise;

                if (error instanceof UploadReason && !(error instanceof UploadCancel)) {
                    promise = backendDispatcher.updateUploadUrl();
                } else {
                    promise = Promise.reject(error);
                }

                return promise;
            });

            // Повторяет загрузку файла.
            promise = promise.then(function (url) {
                uploader._url = url;
                uploader._upload();
            });

            promise = promise.catch(function (error) {
                const data = uploader._getData();

                data.error = error; // TODO: keep previous error?
                uploader._fail(data);
            });
        },

        /**
         * @param {File|FormData} body
         */
        async _makeRequest(body) {
            const uploader = this;

            const descriptor = this._descriptor;
            if (descriptor) {
                descriptor.set({
                    startTime: performance.now(),
                });
            }

            if (!isUploadResumeAvailabe) {
                const xhr = new XMLHttpRequest();

                uploader._xhr = xhr;

                xhr.open(Uploader.HTTP_METHOD, uploader._url);
                xhr.withCredentials = true;
                xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
                uploader._listenXhr(xhr);
                uploader._uploadStartTime = Date.now();

                xhr.send(body);
                return;
            }

            // http://cloud.pages.gitlab.corp.mail.ru/api/uploader

            // 1. check file status on the server
            this._axios = null;

            let receivedBytes = 0;
            let recheckUploadCompleteTimeMs = 0;
            let url = uploader._url;
            try {
                this._axios = new UploadResumableCheckAPICall();
                const res = await this._axios.makeRequest(null, {
                    url,
                    headers: {
                        'Content-Range': `bytes */${body.size}`,
                    },
                });

                switch (res.status) {
                    case 200: // partialy uploaded
                        receivedBytes = Number.parseInt(res.headers['x-received']) || 0;
                        url = res.headers['x-location'] || url;
                        sendGaUploader('waspartialy-upldd');

                        break;
                    case 201: {
                        // uploaded. (Client should confirm ownship?)
                        // continue to add file
                        uploader._xhr = res;
                        const data = uploader._getData();
                        data.responseText = uploader._hash;
                        uploader._onXhrLoad(res, data);
                        sendGaUploader('wasprvs-upldd');

                        sendMetricsToDwh(uploader, body.size, body.size);

                        return;
                    }
                    case 202: {
                        // uploaded and is moving to storage, recheck it
                        uploader._xhr = res;
                        const data = uploader._getData();
                        data.responseText = uploader._hash;
                        recheckUploadCompleteTimeMs = getWaitTimeFromHeader(res.headers['x-wait-for']);

                        break;
                    }
                }
            } catch (err) {
                switch (err.status) {
                    case 404: // file not found on the sever
                        break;
                    case 400: // upload error
                        logger.error('upload check status error: ', 400, err.headers ? err.headers['x-upload-error'] : '');
                        uploader._onXhrAbort({}, {});
                        return;
                    default:
                        logger.error('upload check status error: ', err.status);
                        if (!window.navigator.onLine || err.status === 0) {
                            uploader._xhr = err;
                            const data = uploader._getData();
                            this._onXhrError({}, data);
                            sendGaUploader('uploadchk_err_0');
                        } else {
                            uploader._onXhrAbort({}, {});
                        }
                        return;
                }
            }

            // 2. upload file
            let warning504 = false;
            if (recheckUploadCompleteTimeMs <= 0) {
                uploader._uploadStartTime = Date.now();

                try {
                    this._axios = new UploadResumableCheckAPICall();
                    const res = await this._axios.makeRequest(receivedBytes ? body.slice(receivedBytes) : body, {
                        url,
                        headers: { 'Content-Range': `bytes ${receivedBytes}-${body.size - 1}/${body.size}` },
                        onUploadProgress: (event) => {
                            uploader._xhr = res;
                            const data = uploader._getData?.() ?? {};
                            data.startBytes = receivedBytes;
                            data.size = body.size;
                            uploader._onXhrProgress?.(event, data);
                        },
                    });

                    sendGaUploader(`uploadfinish_succ_${res.status}`);

                    if (res.status === 202) {
                        recheckUploadCompleteTimeMs = getWaitTimeFromHeader(res.headers['x-wait-for']);
                    }

                    uploader._xhr = res;
                } catch (err) {
                    if (err.status === 400) {
                        sendGaUploader('uploadfinish_err');
                    }

                    sendGaUploader(`uploadfinish_err_${err.status}`);

                    if (err.status === 504) {
                        recheckUploadCompleteTimeMs = getWaitTimeFromHeader();
                        sendGaUploader('warning-504');
                        warning504 = true;
                    } else {
                        uploader._xhr = err;
                        const data = uploader._getData();
                        this._onXhrError({}, data);
                        logger.error('upload error: ', err.status, err.headers ? err.headers['x-upload-error'] : '');
                        return;
                    }
                }
            }

            uploader._metrics.putTime = uploader._uploadStartTime ? getPerfDiffInMs(uploader._uploadStartTime) : 0;

            // 3. continue to file add
            const pollStartTime = Date.now();
            if (recheckUploadCompleteTimeMs > 0) {
                const pollStartTime = performance.now();
                for (let i = 0; i < UPLOAD_API_POLLING_TIME / UPLOAD_API_PING_INTERVAL; i++) {
                    try {
                        if (performance.now() - pollStartTime > 5 * 60 * 1000) {
                            sendGaUploader('check-warning-5m');
                        }

                        this._axios = new UploadResumableCheckAPICall();
                        const res = await this._axios.makeRequest(null, {
                            url,
                            headers: {
                                'Content-Range': `bytes */${body.size}`,
                            },
                        });

                        if (res.status === 201) {
                            // uploaded. (Client should confirm ownship?)
                            // continue to add file
                            if (warning504) {
                                // После 504 (таймаут не беке) проверка показала, что файл все-таки был успешно загружен
                                sendGaUploader('warning-504-loaded');
                            }
                            uploader._xhr = res;
                            break;
                        } else if (res.status === 202) {
                            if (i >= UPLOAD_API_POLLING_TIME / UPLOAD_API_PING_INTERVAL) {
                                sendGaUploader('check-timeout');
                                logger.error(`cannot get 201 status during ${UPLOAD_API_POLLING_TIME}`);
                                uploader._onXhrAbort({}, {});
                                break;
                            }
                            await new Promise((resolve) => setTimeout(resolve, UPLOAD_API_PING_INTERVAL));
                        }
                    } catch (err) {
                        logger.error('upload check status error: ', err.status);
                        uploader._onXhrAbort({}, {});
                        return;
                    }
                }
            }

            uploader._metrics.pollTime = getPerfDiffInMs(pollStartTime);

            sendMetricsToDwh(uploader, body.size, receivedBytes);

            const data = uploader._getData();
            data.responseText = uploader._hash;
            uploader._onXhrLoad(uploader._xhr, data);

            this._axios = null;
        },

        _timeout() {
            sendGaUploader('timeout', 'error', { message: this._timeOutStep || '' });
            sendKaktamLog({ message: this._timeOutStep || '' });
        },

        /**
         * @returns {Promise}
         * @resolves {UploaderData}
         * @rejects {UploaderData}
         */
        _upload() {
            const uploader = this;

            uploader._timeOutTimerId = setTimeout(uploader._timeout.bind(uploader), 30000);
            const stopTimeoutTimer = () => {
                if (uploader._timeOutTimerId) {
                    clearTimeout(uploader._timeOutTimerId);
                    uploader._timeOutTimerId = null;
                    uploader._timeOutStep = null;
                }
            };

            // Проверяет, что пользователь авторизован
            let promise = env.checkUserAuth();
            uploader._timeOutStep = 'checkUserAuth';

            promise = promise.then(function () {
                return uploader._file;
            });

            // Проверяет, что файл до сих пор доступен в локальной ФС.
            promise = promise.then(function (file) {
                let promise;
                const isEdge = env.isEdge;

                // в edge ограниченое кол-во считываний entry и fileread
                if (isEdge && !file.type) {
                    const source = UploadReason.SOURCE_WEB_CLIENT;
                    const stack = new Error('UnsupportedFolderTransferFail');
                    const reason = new UnsupportedFolderTransferFail(stack, source);
                    promise = Promise.reject(reason);
                } else if (isEdge || file.size > fileSystemFileReader.MAX_READABLE_SIZE) {
                    promise = Promise.resolve(file);
                } else {
                    // Выполняем проверку только для небольших файлов,
                    // чтобы не получить исключение "out of memory".
                    promise = fileSystemFileReader.readFile(file);
                    uploader._timeOutStep = 'readFile';
                }

                return promise;
            });

            // Проверяет, удовлетворяет ли файл ограничениям Облака:
            // • Не больше 32GB.
            // • Не превышает лимит пользователя (2GB для бесплатного тарифа).
            // • Не превышает значение `upload_limit` для пабликов с кошарингом
            promise = promise.then(function (file) {
                if (app.isPublic()) {
                    uploader._timeOutStep = 'checkFileSizeLessThanPublicLimit';
                    return cloudFileSystem.checkFileSizeLessThanPublicLimit(file.size);
                } else {
                    uploader._timeOutStep = 'checkFileSizeLessThanLimit';
                    return cloudFileSystem.checkFileSizeLessThanLimit(file.size);
                }
            });

            // Проверяет, удовлетворяет ли файл ограничениям браузера:
            // • Не больше 4GB в IE.
            promise = promise.then(function (size) {
                uploader._timeOutStep = 'checkFileSizeLessThanLimit';
                return env.checkFileSizeLessThanLimit(size);
            });

            // Проверяет, достаточно ли места в Облаке.
            promise = promise.then(function (size) {
                // На пабликах проверяем квоту на бекенде, ибо файлы грузятся в облако хозяина, а доступа к ней мы не имеем
                if (app.isPublic()) {
                    return Promise.resolve();
                }

                // CLOUDWEB-11854: на реакт-страницах не работают методы из модуля Folder поэтому пока пропускаем эту проверку
                if (!IS_REACT_PAGE && (app.user.isB2BUserCorp() || app.user.isOldBizUser())) {
                    const folder = app.get(cloudFileSystem.getWorkingDirectory());
                    if (
                        folder &&
                        (app.isDomainFolder(folder.id) ||
                            app.foldersBreadcrumbs(null, folder.parent).some((item) => app.isDomainFolder(item.id)))
                    ) {
                        // Для бизов в общие папки не проверяем личную квоту юзера
                        return Promise.resolve();
                    }
                }

                uploader._timeOutStep = 'checkEnoughSpace';
                return cloudFileSystem.checkEnoughSpace(size);
            });

            // Для файлов размером меньше 21 байт
            // вместо hash в базу помещается байткод.
            promise = promise.then(function () {
                const size = uploader._file.size;
                let promise;

                if (size > backendRequest.FILE_WITHOUT_HASH_MAX_SIZE) {
                    promise = Promise.resolve(size);
                } else {
                    const source = UploadReason.SOURCE_WEB_CLIENT;
                    const stack = new Error('FileWithoutHashFail');
                    const reason = new FileWithoutHashFail(stack, source);

                    promise = Promise.reject(reason);
                }

                return promise;
            });

            // Получает от балансировщика адрес сервера для загрузки файла.
            promise = promise.then(function () {
                let promise;

                if (uploader._url) {
                    promise = Promise.resolve(uploader._url);
                } else {
                    promise = backendDispatcher.getUploadUrl();
                    uploader._timeOutStep = 'getUploadUrl';
                }

                return promise;
            });

            // Запоминает адрес сервера для загрузки файла.
            promise = promise.then(function (url) {
                uploader._url = url;
            });

            // Получает данные файла.
            promise = promise.then(function () {
                const file = uploader._file;
                uploader._timeOutStep = 'getFullBody';

                return backendRequest.getFullBody(file, Uploader.HTTP_METHOD);
            });

            uploader._metrics = {
                diskReadTime: 0,
                hashCalcTime: 0,
                startTime: Date.now(),
            };

            let hashInitTime = 0;

            // Вычисляем хеш файла на клиенте для нового апи аплоада
            if (isUploadResumeAvailabe && uploader._file.size > backendRequest.FILE_WITHOUT_HASH_MAX_SIZE) {
                this.emit(Uploader.EVENT_PROGRESS, { progress: 0, loaded: 1 });

                let hashLib = EHashLibType.js;

                // На файле больше 16ГБ примерно падает ошибка, по памяти - надо потом чинить
                if (isHashCalcLibWasm && uploader._file.size <= 10000 * MB) {
                    hashLib = EHashLibType.wasm;
                }
                if (isHashCalcLibCrypto && uploader._file.size <= fileReadBlockLength) {
                    hashLib = EHashLibType.cryptoApi;
                }

                const processHash = (hashCalculator, file, startIdx, stopIdx) => {
                    const startRead = Date.now();

                    return fileSystemFileReader
                        .readFileContent(file, startIdx, stopIdx)
                        .catch((err) => {
                            sendGaUploader(`readfile_error${err.name ? '_' + err.name : ''}`);
                            const stack = new Error('FileReaderError');
                            const reason = new FileReaderError(stack, UploadReason.SOURCE_WEB_CLIENT);
                            return Promise.reject(reason);
                        })
                        .then((data) => {
                            stopTimeoutTimer();

                            uploader._metrics.diskReadTime += getPerfDiffInMs(startRead);

                            const startHash = Date.now();

                            return hashCalculator.addData(data, hashLib).then(() => {
                                uploader._metrics.hashCalcTime += getPerfDiffInMs(startHash);
                            });
                        })
                        .catch((err) => {
                            stopTimeoutTimer();
                            sendGaUploader(`hashcalc_error${err.name ? '_' + err.name : ''}`);
                            const stack = new Error('HashCalcError');
                            const reason = new HashCalcError(stack, UploadReason.SOURCE_WEB_CLIENT);
                            return Promise.reject(reason);
                        });
                };

                promise = promise.then((file) => {
                    const start = Date.now();

                    const hashCalculator = new HashCalculator();
                    uploader._timeOutStep = 'hashCalculator.init';
                    let deferred = hashCalculator.init(hashLib);

                    const startIdx = 0;
                    const lastIdx = file.size - 1;
                    for (let idx = startIdx; idx <= lastIdx; idx += fileReadBlockLength) {
                        deferred = deferred.then(() => {
                            if (hashInitTime === 0) {
                                hashInitTime += getPerfDiffInMs(start);
                            }

                            uploader._timeOutStep = 'readFileContent';

                            return processHash(hashCalculator, file, idx, Math.min(idx + fileReadBlockLength - 1, lastIdx));
                        });
                    }

                    deferred = deferred.then(() => {
                        uploader._timeOutStep = 'hashCalculator.addData';
                        return hashCalculator.addData(file.size.toString(), hashLib);
                    });

                    deferred = deferred.then(() => {
                        uploader._timeOutStep = 'hashCalculator.getHash';

                        const time = Date.now();

                        return hashCalculator.getHash().then((hash) => {
                            const hashLast = getPerfDiffInMs(time);

                            uploader._metrics.hashCalcTime += hashLast + hashInitTime;

                            return hash;
                        });
                    });

                    deferred = deferred.then((hash) => {
                        uploader._hash = hash;
                        uploader._url = uploader._url.replace('[HASH]', uploader._hash);

                        // Return file after hash calc
                        return file;
                    });

                    return deferred;
                });
            }

            // Выполняет загрузку файла.
            promise = promise.then(function (body) {
                stopTimeoutTimer();
                return uploader._makeRequest(body);
            });

            // Для файлов размером меньше 21 байт,
            // вместо hash возвращаем байткод в шестнадцатеричном виде,
            // дополненный нулями до длины облачного hash (40 байт).
            promise = promise.catch(function (reason) {
                let promise;

                stopTimeoutTimer();

                if (reason instanceof FileWithoutHashFail) {
                    const file = uploader._file;

                    if (file.size) {
                        promise = backendRequest.getHexCode(file);
                    } else {
                        promise = Promise.resolve(cloudFileSystem.EMPTY_FILE_HASH);
                    }
                } else {
                    promise = Promise.reject(reason);
                }

                promise = promise.then(function (hexCode) {
                    const data = uploader._getData();

                    data.hash = hexCode;
                    data.size = uploader._file.size;
                    uploader._done(data);
                });

                promise.catch(function (reason) {
                    const data = uploader._getData();

                    data.error = reason;
                    uploader._fail(data);
                });
            });
        },

        /**
         * @returns {Promise}
         * @resolves {UploaderData}
         * @rejects {UploaderData}
         */
        upload() {
            const uploader = this;
            const fileSize = uploader._file.size;

            let promise = new Promise(function (resolve, reject) {
                uploader._resolve = resolve;
                uploader._reject = reject;

                uploader._upload();
            });

            // Проверяет размер и hash файла после загрузки.
            promise = promise.then(function (data) {
                let promise;
                const hasWrongSize = fileSize !== data.size;
                const hasWrongHash = fileSize && data.hash === cloudFileSystem.EMPTY_FILE_HASH;

                if (hasWrongSize || hasWrongHash) {
                    // log initial size too
                    data = Object.assign(
                        {
                            initialSize: fileSize,
                        },
                        data
                    );

                    const message = JSON.stringify(data);
                    const source = UploadReason.SOURCE_BACKEND;
                    const stack = new Error('WrongResultError');
                    const reason = new WrongResultError(stack, source, message);

                    promise = Promise.reject(reason);
                } else {
                    promise = Promise.resolve(data);
                }

                return promise;
            });

            return promise;
        },

        cancel() {
            const data = this._getData();
            const xhr = this._xhr;

            if (xhr) {
                const readyState = xhr.readyState;
                const isCancelable = Uploader.isCancelableXhrState(readyState);

                if (isCancelable) {
                    xhr.abort();
                } else if (readyState !== XMLHttpRequest.DONE) {
                    this._onXhrAbort({}, data);
                }
            } else {
                this._axios?.cancel();
                this._onXhrAbort({}, data);
            }
        },

        /**
         * @param {UploaderData} data
         */
        _done(data) {
            this._resolve(data);
            this._destroy();
        },

        /**
         * @param {UploaderData} data
         */
        _fail(data) {
            if (this._reject) {
                this._reject(data);
                this._destroy();
            }
        },

        _destroy() {
            const xhr = this._xhr;

            if (xhr) {
                this._stopListeningXhr(xhr);

                if (Uploader.isCancelableXhrState(xhr.readyState)) {
                    xhr.abort();
                }

                this._xhr = null;
            }

            this._resolve = null;
            this._reject = null;
            this._file = null;
        },
    });

    module.exports = Uploader;
});
